import React, { useMemo } from 'react';
import { Circle, Marker, Tooltip } from 'react-leaflet';
import styled, { DefaultTheme, useTheme } from 'styled-components';
import { shallowEqual, useSelector } from 'react-redux';
import L from 'leaflet';

import { AppState } from '../../../store/interfaces';
import { Cluster } from '../../../types/cluster';
import { isMarker, MarkerStatus } from '../../../types/marker';

type ClusterType = 'small' | 'big' | 'medium';

const getSize = (): ClusterType => {
  return 'big';
};

const MarkerTooltip = styled(Tooltip)`
  border-radius: 50%;
  width: 12px;
  height: 12px;
  display: flex;
  justify-content: center;
  align-items: center;
  background: transparent;
  border: none;
  box-shadow: unset;

  &::before {
    display: none;
  }

  span {
    color: #000;
    font-size: 16px;
    letter-spacing: -0.4px;
  }
`;

export const Clusters: React.FC = React.memo(function () {
  const theme: DefaultTheme = useTheme();
  const { showBigClusters, clusters, markers, newPin, selectedObject } = useSelector(
    (app: AppState) => ({
      showBigClusters: app.map.layers.bigClusters,
      clusters: app.map.clusters.list,
      markers: app.map.markers.list,
      newPin: app.map.newPin,
      selectedObject: app.map.selectedObject,
    }),
    shallowEqual
  );

  const mapClusters = useMemo(() => {
    const clustersMap: {
      [key: string]: Cluster;
    } = {};

    const getIndex = (cluster) => `${cluster.lat}-${cluster.lng}`;

    clusters.forEach((cluster) => {
      clustersMap[getIndex(cluster)] = cluster;
    });

    if (newPin) {
      return Object.values(clustersMap);
    }

    if (
      selectedObject &&
      isMarker(selectedObject) &&
      selectedObject.status === MarkerStatus.inactive
    ) {
      return Object.values(clustersMap);
    }

    markers.forEach((marker) => {
      marker.clusters.forEach((cluster) => {
        clustersMap[getIndex(cluster)] = cluster;
      });
    });

    return Object.values(clustersMap);
  }, [markers, clusters, newPin, selectedObject]);

  if (mapClusters.length === 0) {
    return null;
  }

  return (
    <>
      {mapClusters.map((cluster) => {
        const minRadius = cluster.radius;
        const size = getSize();

        if (!showBigClusters) {
          return null;
        }

        const { color, opacity } = theme.map.clusters[size];
        const icon = L.icon({
          iconUrl: `/assets/map/cluster_${size}.svg`,
          iconSize: [24, 24],
        });

        return (
          <React.Fragment key={`${cluster.lat}-${cluster.lng}`}>
            <Marker
              zIndexOffset={1}
              icon={icon}
              position={{ lat: cluster.lat, lng: cluster.lng }}
              autoPan={false}
            >
              <MarkerTooltip offset={[0, 18]} direction="top" opacity={1} permanent sticky>
                <span>{cluster.count_rides}</span>
              </MarkerTooltip>
            </Marker>
            {[0, 5, 10, 15, 20].map((value) => (
              <Circle
                key={`${cluster.lat}${cluster.lng}1${value}`}
                center={{
                  lat: cluster.lat,
                  lng: cluster.lng,
                }}
                radius={minRadius - (minRadius * value) / 100}
                weight={5}
                stroke={false}
                pathOptions={{ fillColor: color, fillOpacity: opacity }}
              />
            ))}
          </React.Fragment>
        );
      })}
    </>
  );
});
